/**
 * SinceLastVisit — calm operational continuity card.
 *
 * Aligned to the approved ChainFlow Deal Room visual identity:
 * - clean white card surface (matches Outstanding Actions / Exchange Readiness)
 * - dark slate heading + count badge on the right
 * - simple horizontal rows: icon tile, title, sub, right-aligned timestamp
 * - restrained tone palette across categories (positive / neutral / watch / risk)
 * - NO Slack feel, NO red dots, NO "NEW" tags, NO numeric counters in header
 */
import React from "react";
import {
  Sparkles, FileText, ShieldCheck, Users, Clock, MessageSquare,
  KeyRound, Target, ArrowRight, CheckCircle2, ChevronDown, ChevronUp,
} from "lucide-react";
import { useNewItemTracker } from "../../hooks/useNewItemTracker";

/* Tone tokens — kept restrained so this never feels like alerts. */
const TONE = {
  positive: { fg: "#047857", bg: "#ECFDF5" },
  neutral:  { fg: "#475569", bg: "#F1F5F9" },
  watch:    { fg: "#A16207", bg: "#FFFBEB" },
  risk:     { fg: "#B91C1C", bg: "#FEF2F2" },
};

const ICON = {
  spark: Sparkles, file: FileText, shield: ShieldCheck, user: Users,
  clock: Clock, message: MessageSquare, key: KeyRound, stage: CheckCircle2,
  target: Target,
};

const CATEGORY_LABEL = {
  progression:  "Progression",
  documents:    "Documents",
  risk:         "Risk",
  participants: "Participants",
  scheduling:   "Scheduling",
};

const CATEGORY_ORDER = ["progression", "documents", "risk", "participants", "scheduling"];

const formatRelative = (iso) => {
  if (!iso) return "";
  const dt = new Date(iso);
  const diff = Date.now() - dt.getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  const days = Math.floor(diff / 86_400_000);
  if (days < 7) return `${days}d`;
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const formatSinceLabel = (iso) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  const diff = Date.now() - dt.getTime();
  if (diff < 86_400_000) return "earlier today";
  const days = Math.floor(diff / 86_400_000);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const Item = ({ item, isNew }) => {
  const t = TONE[item.tone] || TONE.neutral;
  const Icon = ICON[item.icon] || Sparkles;
  return (
    <li
      data-testid={`slv-item-${item.id}`}
      data-new={isNew ? "true" : undefined}
      className={`flex items-start gap-3 py-2.5 first:pt-0 last:pb-0 ${isNew ? "cf-settle" : ""}`}
    >
      <span
        className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 relative"
        style={{ background: t.bg, color: t.fg }}
      >
        <Icon size={12} />
        {isNew && (
          <span
            aria-hidden="true"
            className="cf-new-dot absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full"
            style={{ background: "#6366F1" }}
          />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-semibold leading-snug" style={{ color: "#0F172A" }}>
          {item.title}
        </p>
        {item.detail && (
          <p className="text-[11px] leading-snug mt-0.5" style={{ color: "#64748B" }}>
            {item.detail}
          </p>
        )}
      </div>
      <span
        className="shrink-0 text-[10.5px] font-semibold tabular-nums whitespace-nowrap mt-1.5"
        style={{ color: "#94A3B8" }}
      >
        {formatRelative(item.at)}
      </span>
    </li>
  );
};

const FirstVisitState = () => (
  <div data-testid="slv-first-visit" className="text-center py-5">
    <div
      className="w-9 h-9 rounded-full mx-auto mb-2.5 flex items-center justify-center"
      style={{ background: "#EEF2FF", color: "#6366F1" }}
    >
      <Sparkles size={14} />
    </div>
    <p className="text-[12.5px] font-semibold" style={{ color: "#0F172A" }}>Bookmark set</p>
    <p className="text-[11px] mt-1 leading-relaxed" style={{ color: "#64748B" }}>
      Operational movement on this deal will appear here next time you return.
    </p>
  </div>
);

const CaughtUpState = ({ since }) => (
  <div data-testid="slv-caught-up" className="text-center py-5">
    <div
      className="w-9 h-9 rounded-full mx-auto mb-2.5 flex items-center justify-center"
      style={{ background: "#ECFDF5", color: "#059669" }}
    >
      <CheckCircle2 size={14} />
    </div>
    <p className="text-[12.5px] font-semibold" style={{ color: "#0F172A" }}>You're caught up</p>
    <p className="text-[11px] mt-1 leading-relaxed" style={{ color: "#64748B" }}>
      No operational movement since {formatSinceLabel(since)}.
    </p>
  </div>
);

const CategoryPill = ({ category, count }) => {
  const label = CATEGORY_LABEL[category] || category;
  const tone =
    category === "risk"      ? TONE.risk
    : category === "documents"  ? TONE.positive
    : category === "scheduling" ? TONE.watch
    :                             TONE.neutral;
  return (
    <span
      data-testid={`slv-cat-${category}`}
      className="inline-flex items-center gap-1 px-2 h-[22px] rounded-md text-[10.5px] font-semibold tracking-tight"
      style={{ background: tone.bg, color: tone.fg }}
    >
      {label}
      <span className="font-bold tabular-nums" style={{ color: tone.fg, opacity: 0.85 }}>{count}</span>
    </span>
  );
};

export const SinceLastVisit = ({ data, onMarkSeen }) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const items = (data && data.items) || [];
  const newIds = useNewItemTracker(items.map((i) => i.id));
  if (!data) return null;
  const grouped = data.grouped_count || {};
  const orderedCategories = CATEGORY_ORDER.filter((c) => (grouped[c] || 0) > 0);
  const hasItems = items.length > 0 && !data.is_first_visit;

  return (
    <section
      data-testid="since-last-visit"
      className="rounded-2xl bg-white"
      style={{
        border: "1px solid #E2E8F0",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 4px 14px -8px rgba(15,23,42,0.08)",
      }}
    >
      {/* Header — clean dark heading + count badge (matches Outstanding Actions / Participants) */}
      <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-2">
          <h3 className="font-bold text-[14px] leading-tight" style={{ color: "#0F172A" }}>
            Since Your Last Visit
          </h3>
          {hasItems && (
            <span
              data-testid="slv-count"
              className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-md text-[10.5px] font-bold tabular-nums"
              style={{ background: "#EEF2FF", color: "#4F46E5" }}
            >
              {items.length}
            </span>
          )}
        </div>
        {hasItems && (
          <button
            type="button"
            data-testid="slv-collapse"
            onClick={() => setCollapsed((v) => !v)}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-slate-50"
            aria-label={collapsed ? "Expand" : "Collapse"}
            style={{ color: "#94A3B8" }}
          >
            {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        )}
      </div>

      {/* Subtitle line */}
      <div className="px-5 pb-3 -mt-1">
        <p className="text-[11px] leading-tight" style={{ color: "#64748B" }}>
          {data.is_first_visit
            ? "Bookmark just set"
            : hasItems
              ? `${items.length} operational movement${items.length === 1 ? "" : "s"} since ${formatSinceLabel(data.since)}`
              : `No movement since ${formatSinceLabel(data.since)}`}
        </p>
      </div>

      {/* Category pills */}
      {orderedCategories.length > 0 && hasItems && (
        <div className="px-5 pb-3 flex items-center gap-1.5 flex-wrap">
          {orderedCategories.map((c) => (
            <CategoryPill key={c} category={c} count={grouped[c]} />
          ))}
        </div>
      )}

      {/* Body */}
      <div
        className={`${hasItems ? "border-t" : ""}`}
        style={hasItems ? { borderColor: "#F1F5F9" } : undefined}
      >
        {data.is_first_visit ? (
          <div className="px-5 pb-4">
            <FirstVisitState />
          </div>
        ) : !hasItems ? (
          <div className="px-5 pb-4">
            <CaughtUpState since={data.since} />
          </div>
        ) : !collapsed ? (
          <ul className="px-5 py-2 divide-y" style={{ borderColor: "#F1F5F9" }}>
            {items.map((it) => <Item key={it.id} item={it} isNew={newIds.has(it.id)} />)}
          </ul>
        ) : null}
      </div>

      {/* Footer */}
      {hasItems && (
        <div
          className="px-5 py-3 flex items-center justify-between"
          style={{ borderTop: "1px solid #F1F5F9" }}
        >
          <FreshenedTimestamp at={data.since} />
          <button
            type="button"
            data-testid="slv-mark-seen"
            onClick={onMarkSeen}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-semibold transition-colors hover:bg-indigo-50"
            style={{ color: "#4F46E5" }}
          >
            Mark caught up <ArrowRight size={11} />
          </button>
        </div>
      )}
    </section>
  );
};

/* "Updated just now" — soft fade pulse on every intel refresh tick.
   Re-keyed by minute so it gracefully transitions from "just now" → "1m" → "2m" without theatre. */
const FreshenedTimestamp = ({ at }) => {
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(i);
  }, []);
  const label = React.useMemo(() => {
    if (!at) return "Bookmark updates next visit";
    const ms = Date.now() - new Date(at).getTime();
    if (ms < 60_000) return "Bookmark refreshed just now";
    if (ms < 3_600_000) return `Bookmarked ${Math.floor(ms / 60_000)}m ago`;
    return "Bookmark updates next visit";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [at, tick]);
  return (
    <p
      key={`${at}|${tick}`}
      className="text-[10.5px] cf-freshen"
      style={{ color: "#94A3B8" }}
      data-testid="slv-freshened-timestamp"
    >
      {label}
    </p>
  );
};
